import {CSSProperties} from 'react';

import {LayoutMethod, LayoutType} from './types';

type Coordinates = {
    x: number;
    y: number;
};
export const rectCenter = (rect: DOMRect): Coordinates => ({
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
});

export const applyStyleOffset = (
    style: CSSProperties,
    layout: LayoutType,
    layoutOffset: number,
): CSSProperties => {
    if (layout === 'top' || layout === 'top-left' || layout === 'top-right') {
        return {
            ...style,
            left: style.left,
            top: Number(style.top) - layoutOffset,
        };
    }

    if (
        layout === 'bottom' ||
        layout === 'bottom-left' ||
        layout === 'bottom-right'
    ) {
        return {
            ...style,
            left: style.left,
            top: Number(style.top) + layoutOffset,
        };
    }

    return style;
};

export const getInitialLayoutType = (
    layoutMethod: LayoutMethod,
): LayoutType => {
    if (layoutMethod === 'bottom' || layoutMethod === 'bottom-or-top') {
        return 'bottom';
    }

    if (layoutMethod === 'top-or-bottom') {
        return 'top';
    }

    if (layoutMethod === 'left') {
        return 'left';
    }

    if (layoutMethod === 'modal') {
        return 'modal';
    }

    return 'bottom-right';
};

export const alignLeftOfElement = (
    elementRect: DOMRect,
    containerOuterRect: DOMRect,
) => elementRect.right - containerOuterRect.left;

export const alignRightOfElement = (
    elementRect: DOMRect,
    containerOuterRect: DOMRect,
) => elementRect.left - containerOuterRect.left;

export const alignTopOfElement = (
    elementRect: DOMRect,
    containerOuterRect: DOMRect,
) => elementRect.top - containerOuterRect.top;

export const alignBottomOfElement = (
    elementRect: DOMRect,
    containerOuterRect: DOMRect,
) => elementRect.bottom - containerOuterRect.top;

export const alignLeftCenterOfElement = (
    elementRect: DOMRect,
    containerOuterRect: DOMRect,
) => {
    const elementCenter = rectCenter(elementRect);

    return elementCenter.x - containerOuterRect.left;
};

interface AlignVerticallyCenterOfElementWithinBoundaryProps {
    boundaryRect: DOMRect;
    containerInnerRect: DOMRect;
    containerOuterRect: DOMRect;
    elementRect: DOMRect;
    layoutOffset?: number;
}

export const alignTopVerticallyCenterOfElementWithinBoundary = ({
    boundaryRect,
    containerInnerRect,
    containerOuterRect,
    elementRect,
}: AlignVerticallyCenterOfElementWithinBoundaryProps) => {
    const elementCenter = rectCenter(elementRect);
    const verticallyCenter =
        elementCenter.y -
        containerInnerRect.height / 2 -
        containerOuterRect.top;

    if (verticallyCenter < boundaryRect.top - containerOuterRect.top) {
        return boundaryRect.top - containerOuterRect.top;
    }

    if (
        verticallyCenter + containerInnerRect.height >
        boundaryRect.bottom - containerOuterRect.top
    ) {
        return (
            boundaryRect.bottom -
            containerInnerRect.height -
            containerOuterRect.top
        );
    }

    return verticallyCenter;
};

export const matchWidthOfElement = (
    elementRect: DOMRect,
    matchWidth?: boolean,
) => (matchWidth ? elementRect.width : undefined);

interface HasSpaceProps {
    boundaryRect: DOMRect;
    containerInnerRect: DOMRect;
    elementRect: DOMRect;
    layoutOffset?: number;
}
export const hasSpaceBelow = ({
    boundaryRect,
    containerInnerRect,
    elementRect,
    layoutOffset,
}: HasSpaceProps) =>
    elementRect.bottom + (layoutOffset || 0) + containerInnerRect.height <
    boundaryRect.bottom;

export const hasSpaceAbove = ({
    boundaryRect,
    containerInnerRect,
    elementRect,
    layoutOffset,
}: HasSpaceProps) =>
    elementRect.top - containerInnerRect.height + (layoutOffset || 0) >
    boundaryRect.top;

export const hasSpaceOnTheRight = ({
    boundaryRect,
    containerInnerRect,
    elementRect,
}: HasSpaceProps) =>
    elementRect.right + containerInnerRect.width < boundaryRect.right;
